import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { AUTH_LOGOUT } from "@/store/actions/auth";
import db from "@/firebase/init.js";

export default {
  data() {
    return {
      fbUser: null,
      authReady: false,
      authError: null,
    };
  },
  computed: {
    isFirebaseAuthenticated() {
      return this.authReady && this.fbUser !== null;
    },
    storeFirebaseUser() {
      return this.$store.getters.firebaseUser;
    },
    isStoreFirebaseReady() {
      return this.$store.getters.isFirebaseReady;
    },
  },
  async created() {
    // Initialize auth state on component creation
    await this.initializeAuthState();
  },
  methods: {
    async initializeAuthState() {
      try {
        // Check if auth state is already cached in store
        if (this.isStoreFirebaseReady && this.storeFirebaseUser) {
          this.fbUser = this.storeFirebaseUser;
          this.authReady = true;
          return;
        }

        // Small delay to ensure Firebase is fully initialized
        await new Promise((resolve) => setTimeout(resolve, 100));

        this.fbUser = await this.checkAuthStatus();
        this.authReady = true;
      } catch (error) {
        console.error("Auth initialization failed:", error);
        this.authError = error;
        this.authReady = true; // Still mark as ready to prevent hanging
      }
    },
    checkAuthStatus() {
      return new Promise((resolve, reject) => {
        try {
          const auth = getAuth();

          // Check if user is already available
          if (auth.currentUser) {
            // Create a serializable copy of the user object
            const userCopy = {
              uid: auth.currentUser.uid,
              email: auth.currentUser.email,
              displayName: auth.currentUser.displayName,
              photoURL: auth.currentUser.photoURL,
              emailVerified: auth.currentUser.emailVerified,
            };
            this.$store.commit("setFirebaseUser", userCopy);
            resolve(auth.currentUser);
            return;
          }

          const unsubscribe = onAuthStateChanged(
            auth,
            (user) => {
              console.log("User checked:", user);
              unsubscribe(); // Clean up listener after first trigger

              // Update store with auth state (create serializable copy)
              const userCopy = user
                ? {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    emailVerified: user.emailVerified,
                  }
                : null;
              this.$store.commit("setFirebaseUser", userCopy);

              if (user === null) {
                this.logout();
                resolve(null);
              } else {
                resolve(user);
              }
            },
            (error) => {
              console.error("Auth state error:", error);
              this.$store.commit("setFirebaseUser", null);
              unsubscribe();
              reject(error);
            },
          );
        } catch (error) {
          console.error("Auth check failed:", error);
          this.$store.commit("setFirebaseUser", null);
          reject(error);
        }
      });
    },
    async waitForAuth(timeout = 10000) {
      // Helper method to wait for auth state to be ready with timeout
      return new Promise((resolve, reject) => {
        if (this.authReady) {
          resolve(this.fbUser);
          return;
        }

        const startTime = Date.now();
        const checkAuth = () => {
          if (this.authReady) {
            resolve(this.fbUser);
          } else if (Date.now() - startTime > timeout) {
            reject(new Error("Authentication timeout"));
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      });
    },
    async ensureAuthReady() {
      // Ensure auth is ready before proceeding with Firebase operations
      if (!this.authReady) {
        await this.waitForAuth();
      }
      return this.fbUser;
    },

    async executeWithAuth(callback) {
      try {
        const user = await this.ensureAuthReady();
        if (user) {
          return await callback(user);
        } else {
          throw new Error("Authentication required for this operation");
        }
      } catch (error) {
        console.error("Authentication error:", error);
        this.authError = error.message;
        throw error;
      }
    },
    logout() {
      this.$store.dispatch(AUTH_LOGOUT);
      const auth = getAuth();
      signOut(auth)
        .then(() => {
          // Sign-out successful.
        })
        .catch((error) => {
          console.error("Sign-out error:", error);
          // An error happened.
        });
    },
    async ensureFirestoreReady() {
      // Simplified Firestore validation
      try {
        // Basic check - if db exists, it should be ready
        if (!db) {
          console.warn("Firestore db is null");
          // Add small delay and retry once
          await new Promise((resolve) => setTimeout(resolve, 100));
          if (!db) {
            throw new Error("Firestore database not available");
          }
        }

        // If we reach here, db exists and should be usable
        return db;
      } catch (error) {
        console.warn("Firestore initialization check failed:", error);
        // Still return db even if checks fail, as it might still work
        if (db) {
          console.log("Returning db despite validation concerns");
          return db;
        }
        throw error;
      }
    },

    async ensureFirebaseReady() {
      // Ensure both auth and database are ready
      await this.ensureAuthReady();

      const validatedDb = await this.ensureFirestoreReady();

      if (!validatedDb) {
        throw new Error("Firebase database not initialized");
      }

      return { auth: this.fbUser, db: validatedDb };
    },

    async executeWithFirebase(callback) {
      try {
        const { auth, db: validatedDb } = await this.ensureFirebaseReady();
        if (auth && validatedDb) {
          return await callback(auth, validatedDb);
        } else {
          throw new Error(
            "Firebase authentication and database required for this operation",
          );
        }
      } catch (error) {
        console.error("Firebase operation error:", error);
        this.authError = error.message;
        throw error;
      }
    },
  },
};
